import axios, { AxiosError } from "axios";

import { getCookie } from "@vfuk/core-helpers";

import { getHostName } from "./getHostName";
import { setCookie } from "./setCookie";

export const LOCAL_STORAGE_USER_SESSION_ID_KEY = "userSessionId";

const getCookieSafe = typeof window !== "undefined" ? getCookie : () => null;

const deleteAllLegacyAuthCookies = () => {
  // delete all Legacy Auth Cookies
  // this function can be removed after releasing this code on prod

  setCookie("Token", "", {
    expires: -1,
  });
  setCookie("refreshToken", "", {
    expires: -1,
  });
  setCookie("Header", "", {
    expires: -1,
  });
  sessionStorage.removeItem(LOCAL_STORAGE_USER_SESSION_ID_KEY);
};

const deleteAllAuthCookies = () => {
  // Expires all auth cookies
  deleteAllLegacyAuthCookies();

  setCookie("Token", "", {
    expires: -1,
    domain: getHostName(),
  });
  setCookie("refreshToken", "", {
    expires: -1,
    domain: getHostName(),
  });
  setCookie("Header", "", {
    expires: -1,
    domain: getHostName(),
  });
  sessionStorage.removeItem(LOCAL_STORAGE_USER_SESSION_ID_KEY);
};

// Setup the base service config
type BaseServiceConfig = {
  /** The url to the service for refreshing the token. */
  refreshTokenURL: string;
  /** An optional callback for when the user is denied access to a protected resource. */
  onPermissionDenied?: (axiosError: AxiosError) => void;
  /** An optional callback for when the user regains access to a protected resource. */
  onPermissionRegained?: () => void;
  /** An optional object containing headers to be added to each request. */
  additionalHeaders?: { [key: string]: string };
  /** The url to the portal. */
  portalUrl: string;
};

const COOKIE_EXPIRY_DAYS = 7;
const TIMEOUT_PERIOD = 5 * 60 * 1000; // 5 mins

export const createBaseService = (baseServiceConfig: BaseServiceConfig) => {
  const {
    refreshTokenURL,
    onPermissionDenied = () => null,
    additionalHeaders = {},
    portalUrl,
  } = baseServiceConfig;

  const headers = {
    "Content-Type": "application/json;charset=UTF-8",
    authorization: getCookieSafe("Token") || "",
    refreshToken: getCookieSafe("refreshToken") || "",
    "x-uc-hub-hdr": getCookieSafe("Header") || "",
    ...additionalHeaders,
  };

  let refreshTimerTriggered = false;
  setInterval(() => {
    refreshTimerTriggered = true;
  }, TIMEOUT_PERIOD);

  const baseService = axios.create({
    baseURL: "",
    headers: headers,
  });

  const refreshAccessToken = async () =>
    await baseService.post(refreshTokenURL);

  // Response Interceptor used for handling re-authentication
  baseService.interceptors.response.use(
    async (res) => {
      if (refreshTimerTriggered) {
        refreshTimerTriggered = false;
        // Pre-emptively refresh the access token so that the server timeout is refreshed
        const res = await refreshAccessToken()
          .then((res) => res)
          .catch((err) => {
            return Promise.reject(err.response.data);
          });
        const { token } = res.headers;
        // Set the cookie for future requests.
        setCookie("Token", token, {
          expires: COOKIE_EXPIRY_DAYS,
          domain: getHostName(),
        });
      }
      return res;
    },
    async (err) => {
      const { config, response } = err;

      if (typeof config === "undefined") {
        return Promise.reject(err);
      }

      // If the request has already been retried, return.
      if (config.retry) return;

      // If the response status is 408, then the user is not logged in.
      // If the response status is 403, then the user is not authorized to view the page.
      const isAuthExpired = response.status === 408;
      const isUnauthorized = response.status === 403;

      // If there is unauthorized access, toggle the access denied screen
      if (isUnauthorized) {
        onPermissionDenied(err);
      }

      // If the refreshToken cookie is not present, log out.
      if (!getCookieSafe("refreshToken")) {
        // If the refresh token has expired, log out.
        deleteAllAuthCookies();
        window.location.href = `${portalUrl}/login?sessionExpired`;
        return Promise.reject(err.response.data);
      }

      // If the authentication token has expired, try to refresh it once.
      if (isAuthExpired) {
        config.retry = true;

        // Refresh the access token.
        const res = await refreshAccessToken()
          .then((res) => res)
          .catch((err) => {
            // If the refresh token has expired, log out.
            deleteAllAuthCookies();
            window.location.href = `${portalUrl}/login?sessionExpired`;
            return Promise.reject(err.response.data);
          });

        const { token } = res.headers;

        // Set the cookie for future requests. Note that cookies only refresh on a full page refresh.
        setCookie("Token", token, {
          expires: COOKIE_EXPIRY_DAYS,
          domain: getHostName(),
        });

        // Retry the request with an updated config.
        config.headers.authorization = token;

        return baseService(config);
      }

      return Promise.reject(err);
    }
  );

  // Create an Axios instance
  return baseService;
};

export default createBaseService;
